import { useState } from "react";
import { motion } from "framer-motion";
import OneWayFlightCard from "../OneWayFlightCard";
import InternationalRoundTripFlightCard from "../InternationalRoundTripFlightCard";
import RoundTripFlightCard from "../RoundTripFlightCard";
// import { useAuth } from "../../context/AuthContext";

const FlightCard = ({
  flight,
  index,
  tripType,
  serType,
  expandedFlights,
  toggleFlightDetails,
  setSelectedFlightForPrice,
  setShowPriceModal,
  verifyFlightPrice,
  fetchSSROptions,
  statusRefdId,
  formatApiDateTime,
  minutesToHm,
  handleAuthCheck,
  getSegments,
  getStopsText,
  getTotalDurationMinutes,
  bookWithSSR, // Add this new prop for SSR booking
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [onwardFareOptions, setOnwardFareOptions] = useState(null);
  const [returnFareOptions, setReturnFareOptions] = useState(null);
  const [loadingOnwardFares, setLoadingOnwardFares] = useState(false);
  const [loadingReturnFares, setLoadingReturnFares] = useState(false);
  const [selectedOnwardFare, setSelectedOnwardFare] = useState(null);
  const [selectedReturnFare, setSelectedReturnFare] = useState(null);
  const [showOnwardFares, setShowOnwardFares] = useState(false);
  const [showReturnFares, setShowReturnFares] = useState(false);

  // State for separate flight details expansion
  const [expandedOnwardDetails, setExpandedOnwardDetails] = useState(false);
  const [expandedReturnDetails, setExpandedReturnDetails] = useState(false);
  const [expandedOneWayDetails, setExpandedOneWayDetails] = useState(false);

  // FIRST: Check if this is a domestic round trip (has separate onwardFlight and returnFlight)
  if (flight.isRoundTrip) {
    return (
      <RoundTripFlightCard
        flight={flight}
        index={index}
        tripType={tripType}
        expandedFlights={expandedFlights}
        toggleFlightDetails={toggleFlightDetails}
        setSelectedFlightForPrice={setSelectedFlightForPrice}
        setShowPriceModal={setShowPriceModal}
        verifyFlightPrice={verifyFlightPrice}
        handleAuthCheck={handleAuthCheck}
        statusRefdId={statusRefdId}
        formatApiDateTime={formatApiDateTime}
        minutesToHm={minutesToHm}
        getSegments={getSegments}
        getStopsText={getStopsText}
        getTotalDurationMinutes={getTotalDurationMinutes}
        isExpanded={isExpanded}
        setIsExpanded={setIsExpanded}
        onwardFareOptions={onwardFareOptions}
        setOnwardFareOptions={setOnwardFareOptions}
        returnFareOptions={returnFareOptions}
        setReturnFareOptions={setReturnFareOptions}
        loadingOnwardFares={loadingOnwardFares}
        setLoadingOnwardFares={setLoadingOnwardFares}
        loadingReturnFares={loadingReturnFares}
        setLoadingReturnFares={setLoadingReturnFares}
        selectedOnwardFare={selectedOnwardFare}
        setSelectedOnwardFare={setSelectedOnwardFare}
        selectedReturnFare={selectedReturnFare}
        setSelectedReturnFare={setSelectedReturnFare}
        showOnwardFares={showOnwardFares}
        setShowOnwardFares={setShowOnwardFares}
        showReturnFares={showReturnFares}
        setShowReturnFares={setShowReturnFares}
        expandedOnwardDetails={expandedOnwardDetails}
        setExpandedOnwardDetails={setExpandedOnwardDetails}
        expandedReturnDetails={expandedReturnDetails}
        setExpandedReturnDetails={setExpandedReturnDetails}
        bookWithSSR={bookWithSSR} // Pass to RoundTripFlightCard
      />
    );
  }

  // SECOND: Check if this is an international round trip (single object with both Onward and Return)
  // International flights have both Onward and Return in Flights object
  const hasOnwardFlight = flight?.Flights?.Onward;
  const hasReturnFlight = flight?.Flights?.Return;

  // Check if it's an international round trip
  const isInternationalRoundTrip = hasOnwardFlight && hasReturnFlight;

  if (isInternationalRoundTrip) {
    return (
      <InternationalRoundTripFlightCard
        flight={flight}
        index={index}
        tripType={tripType}
        expandedFlights={expandedFlights}
        toggleFlightDetails={toggleFlightDetails}
        setSelectedFlightForPrice={setSelectedFlightForPrice}
        setShowPriceModal={setShowPriceModal}
        verifyFlightPrice={verifyFlightPrice}
        handleAuthCheck={handleAuthCheck}
        statusRefdId={statusRefdId}
        formatApiDateTime={formatApiDateTime}
        minutesToHm={minutesToHm}
        getSegments={getSegments}
        getStopsText={getStopsText}
        getTotalDurationMinutes={getTotalDurationMinutes}
        isExpanded={isExpanded}
        setIsExpanded={setIsExpanded}
        onwardFareOptions={onwardFareOptions}
        setOnwardFareOptions={setOnwardFareOptions}
        returnFareOptions={returnFareOptions}
        setReturnFareOptions={setReturnFareOptions}
        loadingOnwardFares={loadingOnwardFares}
        setLoadingOnwardFares={setLoadingOnwardFares}
        loadingReturnFares={loadingReturnFares}
        setLoadingReturnFares={setLoadingReturnFares}
        selectedOnwardFare={selectedOnwardFare}
        setSelectedOnwardFare={setSelectedOnwardFare}
        selectedReturnFare={selectedReturnFare}
        setSelectedReturnFare={setSelectedReturnFare}
        showOnwardFares={showOnwardFares}
        setShowOnwardFares={setShowOnwardFares}
        showReturnFares={showReturnFares}
        setShowReturnFares={setShowReturnFares}
        expandedOnwardDetails={expandedOnwardDetails}
        setExpandedOnwardDetails={setExpandedOnwardDetails}
        expandedReturnDetails={expandedReturnDetails}
        setExpandedReturnDetails={setExpandedReturnDetails}
        bookWithSSR={bookWithSSR} // Pass to InternationalRoundTripFlightCard
      />
    );
  }
  // THIRD: Handle one-way flights (both domestic and international one-way)

  if ((serType == 1 && tripType == 0) || (serType == 2 && tripType == 0)) {
    return (
      <OneWayFlightCard
        flight={flight}
        index={index}
        tripType={tripType}
        expandedFlights={expandedFlights}
        toggleFlightDetails={toggleFlightDetails}
        setSelectedFlightForPrice={setSelectedFlightForPrice}
        setShowPriceModal={setShowPriceModal}
        verifyFlightPrice={verifyFlightPrice}
        fetchSSROptions={fetchSSROptions}
        handleAuthCheck={handleAuthCheck}
        statusRefdId={statusRefdId}
        formatApiDateTime={formatApiDateTime}
        minutesToHm={minutesToHm}
        getSegments={getSegments}
        getStopsText={getStopsText}
        getTotalDurationMinutes={getTotalDurationMinutes}
        isExpanded={isExpanded}
        setIsExpanded={setIsExpanded}
        expandedOneWayDetails={expandedOneWayDetails}
        setExpandedOneWayDetails={setExpandedOneWayDetails}
        bookWithSSR={bookWithSSR} // Pass to OneWayFlightCard
      />
    );
  }
};

export default FlightCard;
